import { NoteAttributesType } from "lib/types/checkout";

/**
 * Layers the store script's snapshot over the checkout's current note
 * attributes.
 *
 * The script cannot know what else is on the checkout - UTM attribution, a cart
 * note the shopper typed - so its attributes are merged in by name rather than
 * replacing the list. Existing order is preserved and new names are appended.
 */
export const mergeNoteAttributes = (
  current: NoteAttributesType[],
  incoming: NoteAttributesType[],
): NoteAttributesType[] => {
  const incomingByName = new Map(incoming.map((attribute) => [attribute.name, attribute.value]));
  const merged = (current ?? []).map((attribute) =>
    incomingByName.has(attribute.name)
      ? { name: attribute.name, value: incomingByName.get(attribute.name) as string }
      : attribute,
  );
  const existingNames = new Set((current ?? []).map((attribute) => attribute.name));
  incoming.forEach((attribute) => {
    if (!existingNames.has(attribute.name)) merged.push(attribute);
  });
  return merged;
};

/**
 * Order-insensitive identity for a note attribute list, so an unchanged
 * snapshot can be recognised and skipped instead of costing a request.
 */
export const serializeNoteAttributes = (attributes: NoteAttributesType[]): string =>
  JSON.stringify((attributes ?? []).map((attribute) => [attribute.name, attribute.value]).sort());

/**
 * Names this iframe previously wrote that are absent from the new snapshot.
 *
 * A merge on its own can only add and overwrite, so without this
 * removeOrderNoteAttributes would never reach a checkout that already exists.
 */
export const findDroppedNames = (pushedNames: Set<string>, incoming: NoteAttributesType[]): string[] => {
  const incomingNames = new Set(incoming.map((attribute) => attribute.name));
  return Array.from(pushedNames).filter((name) => !incomingNames.has(name));
};

/**
 * Folds a batch of queued operations onto the current list.
 *
 * Removals are applied before the merge, so a name removed and re-added in the
 * same batch survives. `updateOnly` names - those queued by editNoteAttributes -
 * are dropped when the checkout does not already carry them, which is what
 * stops an edit from creating an attribute.
 */
export const resolveNoteAttributes = ({
  base,
  upserts,
  removals,
  updateOnly,
}: {
  base: NoteAttributesType[];
  upserts: NoteAttributesType[];
  removals: Set<string>;
  updateOnly: Set<string>;
}): NoteAttributesType[] => {
  const existingNames = new Set((base ?? []).map((attribute) => attribute.name));
  const kept = (base ?? []).filter((attribute) => !removals.has(attribute.name));
  const applicable = upserts.filter(
    (attribute) => !updateOnly.has(attribute.name) || existingNames.has(attribute.name),
  );
  return mergeNoteAttributes(kept, applicable);
};
